import type { ComponentProps } from 'react'
import React, { useCallback, useMemo } from 'react'
import { isRoomsInvalid } from 'lib/search/searchUtils'
import TextButton from 'LuxKit/Button/TextButton'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getDurationPackageOptions } from 'selectors/offerPage/offerPageSelectors'
import { hasSelectedRooms } from 'lib/offer/offerPageUtil'
import { useIntl } from 'react-intl'

interface Props extends Omit<ComponentProps<typeof TextButton>, 'kind' | 'children'> {
  rooms: Array<App.RoomOccupants>
  checkIn?: string
  packageOptions: Array<App.OfferPackageOptionView>
  isFlightSelected?: boolean
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  durationSelected?: boolean
}

function FlashHotelAvailabilityButton(props: Props) {
  const {
    rooms,
    checkIn,
    isFlightSelected,
    onClick,
    durationSelected,
    ...rest
  } = props

  const intl = useIntl()
  const offerPageDispatch = useOfferPageDispatch()

  const invalidRoomSelection = !hasSelectedRooms(rooms) || isRoomsInvalid(rooms)
  const canViewOptions = !!checkIn && !invalidRoomSelection
  const ctaLabel = useMemo(() => {
    if (canViewOptions) {
      if (rooms.length > 1) {
        return intl.formatMessage(
          {
            id: 'eDfJJQuA-doSRuVwf8su',
            defaultMessage: 'View {count, plural, one {# room} other {# rooms}}',
            description: 'Button text to view multiple rooms on the hotel or room booking page',
          },
          { count: rooms.length },
        )
      } else {
        return intl.formatMessage({
          id: 'CsM-BWhTHtDxES45Yg_e',
          defaultMessage: 'View rooms',
          description: 'Button text to view the available rooms for the selected hotel offer or property',
        })
      }
    }
    return intl.formatMessage({
      id: '_etWrwIdpiw96KvQ_Pzp',
      defaultMessage: 'Check availability',
      description: 'Button text to check availability on the hotel or room booking page',
    })
  }, [canViewOptions, rooms, intl])

  const onClickAvailability = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(e)
    if (invalidRoomSelection || !durationSelected) {
      offerPageDispatch({
        type: OfferPageActions.checkAvailability,
        filter: 'capacity',
        flights: !!isFlightSelected,
      })
      return
    }

    if (!checkIn) {
      offerPageDispatch({
        type: OfferPageActions.checkAvailability,
        filter: 'checkin',
        flights: !!isFlightSelected,
      })

      return
    }

    // Reset previous room selected
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: undefined })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'roomSelection' })
  }, [checkIn, durationSelected, invalidRoomSelection, isFlightSelected, offerPageDispatch, onClick])

  // Horizontal slider mode hides the "View rooms" path when dates are selected
  if (canViewOptions) {
    return null
  }

  return (
    <TextButton {...rest} kind="primary" onClick={onClickAvailability}>
      {ctaLabel}
    </TextButton>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  rooms: state.rooms,
  checkIn: state.checkInDate,
  packageOptions: getDurationPackageOptions(state, appState),
  isFlightSelected: state.flights,
  activeFilter: state.activeFilter,
  durationSelected: state.durationSelected,
}), FlashHotelAvailabilityButton)
